import React, {useContext, useState, useEffect, useMemo, useRef} from 'react';
import {useSelectSong} from "../../navigation/components/Navigation";
import {useApplicationEvents} from "../../../infrastructure/events/useBroadcastChannel";
import 'flag-icons/css/flag-icons.min.css';
import {calculateNextKey} from "../services/chord-operations";
import {getLocalizedKey} from '../services/line-processor'; // Added internationalToPolish back
import {AppCtx, ViewTypes} from "../../../AppCtx";
import Select from 'react-select';
import { BsChevronDown, BsChevronUp } from 'react-icons/bs';
import { FaYoutube, FaExternalLinkAlt, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import {IconUsers, IconPresentation} from '../../../shared/components/HosannaIcons';
import SongActionsMenu from './SongActionsMenu';
import './SongComponent.css'; // Import the CSS file
import {useWorkspaceReadOnly} from '../../../shared/hooks/useWorkspaceReadOnly';
import {isSongAnnotation, songHeaderMetadata} from '../services/song-header-metadata';
import {youtubeEmbedUrl} from '../services/youtube-url';
import {submitAdoransTranspose} from '../services/adorans-transpose';

import DeferredSongBody from './DeferredSongBody';
import {observeSongHeader} from './song-header-measurement';
import {useSongLibrary} from '../library/SongLibraryContext';
import {FavoriteButton} from '../library/SongLibraryPages';
import {revealSongSection} from './reveal-song-section';

const util = {
  isMeta: line => line.type === 'text' && line.content.match(/^\s*\[(((Title|Tag|Artist|Key|DisplayKey|Doc|Notation|Url):)?(.*))\]\s*$/i),
  isEmpty: line => line.type === 'text' && line.content.trim() === '',
  isChord: line => line.type === 'chord',
  isText: line => line.type === 'text',
  isInlineLyrics: line => line.type === 'inline-lyrics',
  isMetaHandled: isSongAnnotation,
};

const FileLine = ({line, songCurrentPosition, areChordsVisible, isLyricSeparatorEnabled}) => {
  const meta = util.isMeta(line);
  const lineEmpty = util.isEmpty(line);
  
  // Handle inline lyrics (chord segments bound to text)
  if (util.isInlineLyrics(line)) {
    const hasNonWhitespaceContent = line.segments && line.segments.some(s => !s.isWhitespace);
    
    return (
      <div 
        id={`line-s${songCurrentPosition}-l${line.lineNumber}`} 
        className={`InlineLyricsLine ${!hasNonWhitespaceContent && isLyricSeparatorEnabled ? 'no-break' : ''}`}
      >
        {line.segments && line.segments.map((segment, i) => {
          // For all whitespace segments, replace spaces with non-breaking spaces to preserve spacing
          const displayText = segment.isWhitespace 
            ? segment.text.replace(/ /g, '\u00A0')
            : segment.text;
          
          return (
            <span 
              key={i} 
              className={`InlineChordSegment ${segment.chord ? 'has-chord' : ''} ${segment.isWhitespace ? 'whitespace-only' : ''}`}
            >
              {segment.chord && areChordsVisible && (
                <span className="InlineChord">{segment.chord}</span>
              )}
              <span className="InlineText">{displayText}</span>
            </span>
          );
        })}
        {hasNonWhitespaceContent && isLyricSeparatorEnabled &&
          <span style={{color:'var(--enter-symbol-color)',fontSize:"0.8em",fontWeight:"bold"}}>↵</span>}
      </div>
    );
  }
  
  if (util.isChord(line) && areChordsVisible) {
    return (
      <div id={`line-s${songCurrentPosition}-l${line.lineNumber}`} className="ChordLine">
        {line.content.map((chord, i) => (
          <React.Fragment key={i}>
            <span className="Prefix">{chord.prefix}</span>
            <span className="Chord">{chord.chord}</span>
            <span className="Suffix">{chord.suffix}</span>
          </React.Fragment>
        ))}
      </div>
    );
  }

  if (meta) {
    if (meta[3]?.toLowerCase() === 'url') {
      const url = meta[4].trim();
      const displayUrl = url.length > 40 ? `${url.slice(0, 37)}...` : url;
      return areChordsVisible && (
        <div id={`line-s${songCurrentPosition}-l${line.lineNumber}`} className="UrlLine">
          <a href={url} className="Tag" target="_blank" rel="noopener noreferrer">{displayUrl}</a>
        </div>
      );
    }
    return (
      <div id={`line-s${songCurrentPosition}-l${line.lineNumber}`} className="MetaLine">
        <span className="Prefix">[</span>
        <span className="Tag">{meta[1]}</span>
        <span className="Suffix">]</span>
      </div>
    );
  }

  if (util.isText(line)) {
    const content = !areChordsVisible && typeof line.content === 'string'
      ? line.content.trim().replace(/\s+/g, ' ') : line.content;

    return (
      <div className={`LyricLine ${!areChordsVisible ? 'extra-spacing bold-text' : ''}
                       ${!lineEmpty && isLyricSeparatorEnabled ? 'no-break' : ''}`}
           id={`line-s${songCurrentPosition}-l${line.lineNumber}`}>
        {content}
        {!lineEmpty && isLyricSeparatorEnabled &&
          <span style={{color:'var(--enter-symbol-color)',fontSize:"0.8em",fontWeight:"bold"}}>↵</span>}
      </div>
    );
  }

  return null;
};

const GroupOfLines = ({group, songCurrentPosition, areChordsVisible, isLyricSeparatorEnabled}) => {

  const filteredGroup = group.filter(line => !util.isMetaHandled(line));
  const onlyMeta = filteredGroup.every(line => util.isMeta(line) || util.isEmpty(line));

  return (
    <div className={`cords-and-text ${onlyMeta ? 'Meta' : 'Lyrics'}
                    ${isLyricSeparatorEnabled && !onlyMeta ? 'no-break' : ''}`}
         id={`group-s${songCurrentPosition}-l${group[0].lineNumber}`}>
      {filteredGroup.map((line, i) => (
        <FileLine key={i} line={line} songCurrentPosition={songCurrentPosition}
                  areChordsVisible={areChordsVisible} isLyricSeparatorEnabled={isLyricSeparatorEnabled}/>
      ))}
    </div>
  );
};

const SectionElements = ({section, songCurrentPosition, areChordsVisible, isLyricSeparatorEnabled, isPresentationControlPanelVisible, isCurrentSection, isPresentedSection, isGroupSection, isActiveGroupSection, onSectionClick, sectionIndex, sectionActions, songTitle}) => {
  // Safety check: ensure section exists and has content array
  if (!section || !section.content || !Array.isArray(section.content) || section.content.length === 0) {
    return null;
  }

  // Safety check: ensure first group has at least one line for ID generation
  const firstLineNumber = section.content[0] && section.content[0][0] 
    ? section.content[0][0].lineNumber 
    : 0;

  const handleClick = () => {
    if (isPresentationControlPanelVisible && onSectionClick) {
      onSectionClick(sectionIndex);
    }
  };

  return (
    <div 
      id={`section-s${songCurrentPosition}-l${firstLineNumber}`}
      className={`section section-${section.typeOfVerse} ${isCurrentSection ? 'current-presentation-section' : ''}${isPresentedSection ? ' section-presentation-target' : ''}${isGroupSection ? ' section-group-target' : ''} ${isPresentationControlPanelVisible ? 'clickable-section' : ''}`}
      onClick={handleClick}
      tabIndex={sectionActions ? -1 : undefined}
      style={{ cursor: isPresentationControlPanelVisible ? 'pointer' : 'default' }}
    >
      {sectionActions && <span className="section-actions" onClick={event => event.stopPropagation()}>
        <button type="button" className={`song-header-action section-action section-action--group${isActiveGroupSection ? ' is-active' : ''}`}
          aria-pressed={!!isActiveGroupSection}
          aria-label={`Wybierz sekcję dla grupy: ${songTitle}, sekcja ${sectionIndex + 1}`}
          title="Pokaż tę sekcję grupie"
          onClick={() => sectionActions.group(sectionIndex, firstLineNumber, `section-s${songCurrentPosition}-l${firstLineNumber}`)}><IconUsers/></button>
        <button type="button" className={`song-header-action section-action section-action--presentation${isPresentedSection && sectionActions.sectionPresented ? ' is-active' : ''}`}
          aria-pressed={!!(isPresentedSection && sectionActions.sectionPresented)}
          aria-label={`Prezentuj sekcję: ${songTitle}, sekcja ${sectionIndex + 1}`}
          title="Pokaż tę sekcję na rzutniku"
          onClick={() => sectionActions.present(sectionIndex)}><IconPresentation/></button>
      </span>}
      {section.content.map((group, i) => (
        <GroupOfLines key={i} group={group} songCurrentPosition={songCurrentPosition}
                      areChordsVisible={areChordsVisible} isLyricSeparatorEnabled={isLyricSeparatorEnabled}/>
      ))}
    </div>
  );
};

const selectStyles = {
  container: base => ({...base, width: 'max-content'}),
  control: base => ({
    ...base,
    backgroundColor: 'var(--background-color)',
    color: 'var(--text-color)',
    border: 'none',
    minHeight: '32px',
    boxShadow: 'none',
  }),
  menu: base => ({
    ...base,
    backgroundColor: 'var(--background-color)',
    color: 'var(--text-color)',
    border: '1px solid var(--border-color)',
    width: '7em',
    maxWidth: 'calc(100vw - 24px)',
    // Centered under the picker instead of hanging off its right edge.
    left: '50%',
    right: 'auto',
    transform: 'translateX(-50%)',
    zIndex: 30,
  }),
  menuList: base => ({...base, overflowX: 'hidden'}),
  singleValue: base => ({
    ...base,
    color: 'var(--secondary-color)',
    fontSize: '1em',
    maxWidth: 'none',
    margin: 0,
    overflow: 'visible',
  }),
  option: (base, state) => ({
    ...base,
    color: state.isSelected ? 'var(--secondary-color)' : 'var(--text-color)',
    backgroundColor: state.isFocused ? 'var(--hover-bg-color)' : 'var(--background-color)', // Added hover effect
    fontSize: '1em',
    padding: '6px 12px',
    whiteSpace: 'nowrap',
    display: 'flex',
    justifyContent: 'center',
  }),
  valueContainer: base => ({
    ...base,
    padding: '3px 8px',
  }),
  input: base => ({...base, margin: 0, padding: 0}),
  indicatorSeparator: base => ({
    ...base,
    margin: '4px 0',
    backgroundColor: 'var(--border-color)' // Theme separator
  }),
  dropdownIndicator: base => ({
    ...base,
    padding: '4px',
    color: 'var(--text-muted)' // Theme indicator
  })
};


const SongComponent = ({song, selectedFile, index, areAllSongsCollapsed, totalSongs, onYoutubeOpen, deferBody = false}) => {
  const [bodyRequested, setBodyRequested] = useState(false);
  const [choosingKey, setChoosingKey] = useState(false);
  const keyTriggerRef = useRef(null);
  const isReadOnly = useWorkspaceReadOnly();
  const {entries, findVariant} = useSongLibrary();
  const libraryEntry = findVariant ? findVariant(selectedFile?.name, song.currentPosition)
    : entries.find(entry => entry.variants.some(variant =>
      variant.fileName === selectedFile?.name && variant.songNumber === song.currentPosition));
  const [isTransposing, setIsTransposing] = useState(false);
  const [transposeError, setTransposeError] = useState('');
  const headerMetadata = useMemo(() => songHeaderMetadata(song), [song]);
  const headerRef = useRef(null);
  const [hideMetadata, setHideMetadata] = useState(false);
  const [hideNotation, setHideNotation] = useState(false);
  const selectSong = useSelectSong();
  const {areChordsVisible, isLyricSeparatorEnabled, collapsedSongs, setCollapsedSongs, isCommonSessionEnabled, isPresentationControlPanelVisible, sharedPresentationState, publishPresentationState, publishGroupSelection, sharedGroupSelection, setViewType} = useContext(AppCtx);
  const transposeRequested = useApplicationEvents('TransposeRequestedEvent');
  const changeNotationRequested = useApplicationEvents('ChangeNotationRequestedEvent');
  const deleteSongRequested = useApplicationEvents('DeleteSongRequestedEvent');
  const editSongPositionRequested = useApplicationEvents('EditSongPositionRequestedEvent');
  
  // Get collapsed state for current file
  const fileCollapsedSongs = collapsedSongs.get(selectedFile?.name) || new Set();
  const isCollapsed = fileCollapsedSongs.has(index);
  
  // Check if this is the current song in presentation
  const isCurrentPresentationSong = process.env.REACT_APP_HOSANNA_DIRECT !== '1' && isPresentationControlPanelVisible && 
    sharedPresentationState?.fileName === selectedFile?.name && 
    sharedPresentationState?.songNumber === song.currentPosition;
  
  const direct = process.env.REACT_APP_HOSANNA_DIRECT === '1';
  const isGroupTarget = direct && sharedGroupSelection?.fileName === selectedFile?.name
    && sharedGroupSelection?.songNumber === song.currentPosition;
  const isWholeGroupSong = isGroupTarget && (!Number.isSafeInteger(sharedGroupSelection?.line)
    || sharedGroupSelection.line === (song.lineStart ?? song.lines?.[0]?.lineNumber ?? 1));
  const groupedSectionIndex = isGroupTarget && !isWholeGroupSong
    ? song.sections.findIndex(section => section?.content?.flat().some(line => line.lineNumber === sharedGroupSelection.line)) : -1;
  const isPresentationTarget = direct && sharedPresentationState?.fileName === selectedFile?.name
    && sharedPresentationState?.songNumber === song.currentPosition;
  const presentedSectionIndex = isPresentationTarget && sharedPresentationState?.kind === 'section'
    ? song.sections.findIndex(section => section?.content?.flat().some(line => line.lineNumber === sharedPresentationState.line)) : -1;
  const groupedSectionStart = song.sections[groupedSectionIndex]?.content?.[0]?.[0]?.lineNumber;
  useEffect(() => {
    if (!isCommonSessionEnabled || isCollapsed || groupedSectionStart == null) return;
    const frame = requestAnimationFrame(() => {
      revealSongSection(document.getElementById(`section-s${song.currentPosition}-l${groupedSectionStart}`));
    });
    return () => cancelAnimationFrame(frame);
  }, [isCommonSessionEnabled, isCollapsed, groupedSectionStart, sharedGroupSelection, song.currentPosition]);
  const [dragOver, setDragOver] = useState(false);
  const dragEnabled = !isReadOnly && areAllSongsCollapsed && !isPresentationControlPanelVisible;

  // Get current section index for highlighting
  const currentSectionIndex = !isCurrentPresentationSong ? -1
    : process.env.REACT_APP_HOSANNA_DIRECT === '1' && sharedPresentationState?.kind === 'section'
      ? song.sections.findIndex(section => section.content.flat().some(line => line.lineNumber === sharedPresentationState.line))
      : typeof sharedPresentationState?.sectionIndex === 'number' ? sharedPresentationState.sectionIndex : -1;
  
  const [editPosition, setEditPosition] = useState(false);
  const [newPosition, setNewPosition] = useState(song.currentPosition);

  useEffect(() => observeSongHeader(headerRef.current, visibility => {
    setHideNotation(visibility.notation);
    setHideMetadata(visibility.metadata);
  }), [song, isCollapsed, areChordsVisible, areAllSongsCollapsed, isReadOnly, isPresentationControlPanelVisible, editPosition, choosingKey]);

  // The processed key is already transposed and localized. DisplayKey is source metadata.
  const displayKey = song.keys[0];
  const originalKeyValue = song.originalKey || song.keys[0];

  // Memoize majorKeys to avoid recreation on every render
  const majorKeys = useMemo(() =>
    ['A', 'A#', 'B', 'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#']
  , []);

  // Memoize keyOptions to avoid recalculation on every render
  const keyOptions = useMemo(() => {
    const getRelativeMinor = (majorKey) => {
      const keyIndex = majorKeys.indexOf(majorKey);
      if (keyIndex === -1) return null;
      const minorIndex = (keyIndex + 9) % 12;
      return majorKeys[minorIndex];
    };

    return majorKeys.map(key => {
      const relativeMinor = getRelativeMinor(key);
      const localizedMajor = getLocalizedKey(key, song.notation);
      const localizedMinor = getLocalizedKey(relativeMinor, song.notation)?.toLowerCase();
      return {
        value: localizedMajor,
        canonicalKey: key,
        majorKey: localizedMajor,
        relativeMinor: localizedMinor,
      };
    });
  }, [song.notation, majorKeys]); // Recalculate when notation or majorKeys changes

  const selectedKeyIndex = keyOptions.findIndex(option => option.value === displayKey);
  const selectedOption = selectedKeyIndex !== -1 ? keyOptions[selectedKeyIndex] : null; // Use index to get selected option, handle -1


  const getOptionLabel = option => `${option.majorKey} / ${option.relativeMinor}`;
  const formatOptionLabel = (option, {context}) => <span className={'song-key-label' +
    (context === 'menu' && option.majorKey === originalKeyValue ? ' song-key-original' : '')}>
    <span className="song-key-major">{option.majorKey}</span>
    <span className="song-key-separator"> / </span>
    <span className="song-key-minor">{option.relativeMinor}</span>
  </span>;

  const handleTranspose = async (toKey) => {
    if (isTransposing) return;
    setTransposeError('');
    if (isReadOnly && toKey !== displayKey) {
      const option = keyOptions.find(item => item.value === toKey);
      if (!option) return;
      setIsTransposing(true);
      try {
        await submitAdoransTranspose({fileName: selectedFile.name, songNumber: song.currentPosition,
          songTitle: song.title, toKey: option.canonicalKey});
      } catch (error) { setTransposeError(error.message); }
      finally { setIsTransposing(false); }
      return;
    }
    // Only transpose if the key actually changes
    if (toKey !== displayKey) {
      transposeRequested.postMessage({
        fromKey: displayKey, // Use current display key as fromKey
        toKey,
        canonicalKey: keyOptions.find(option => option.value === toKey)?.canonicalKey,
        fileName: selectedFile.name,
        index
      });
    }
  };

  const handleSelectChange = (selectedOption) => {
    if (!selectedOption) return; // Handle clear event if applicable
    handleTranspose(selectedOption.value);
  };

  const handleWholeStep = (steps, e) => {
    e.stopPropagation(); // Stop event propagation
    const currentKey = displayKey;
    // Use the non-localized key for calculation if possible, otherwise use the display key
    // Find the international equivalent of the current display key for calculation
    const currentOption = keyOptions[selectedKeyIndex];
    const baseKeyForCalc = currentOption?.canonicalKey || currentKey;

    const nextKey = calculateNextKey(baseKeyForCalc, steps); // Calculate based on international key
    const nextKeyLocalized = getLocalizedKey(nextKey, song.notation);
    handleTranspose(nextKeyLocalized); // Let handleTranspose find the new index
  };

  const toggleNotation = (e) => {
    e.stopPropagation(); // Stop event propagation
    changeNotationRequested.postMessage({
      fileName: selectedFile.name, notation: song.notation === 'polish' ? 'international' : 'polish', index
    });
  }

  const toggleCollapse = e => {
    e.stopPropagation(); // Stop event propagation
    if (!selectedFile?.name) return;
    
    setCollapsedSongs(prev => {
      const newMap = new Map(prev);
      const fileCollapsed = newMap.get(selectedFile.name) || new Set();
      const newSet = new Set(fileCollapsed);
      
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      
      newMap.set(selectedFile.name, newSet);
      return newMap;
    });
  };

  const titleClickHandler = (e) => {
    e.stopPropagation(); // Prevent bubbling further if necessary
    
    setBodyRequested(true);
    toggleCollapse(e);
  };

  const handleDeleteSong = (e) => {
    e?.stopPropagation?.(); // Prevent event propagation
    if (window.confirm(`Czy na pewno chcesz usunąć piosenkę "${song.title}"?`)) {
      deleteSongRequested.postMessage({
        fileName: selectedFile.name,
        songIndex: index
      });
    }
  };

  // Funkcja do obsługi zmiany pozycji piosenki
  const handlePositionChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setNewPosition(value);
    }
  };

  // Funkcja do zapisywania nowej pozycji piosenki
  const saveNewPosition = () => {
    if (newPosition !== song.currentPosition) {
      editSongPositionRequested.postMessage({
        targetFileName: selectedFile.name,
        oldPosition: song.currentPosition,
        newPosition: newPosition
      });
    }
    setEditPosition(false);
  };

  // Obsługa przycisku "w górę"
  const moveSongUp = (e) => {
    e.stopPropagation();
    if (song.currentPosition > 1) {
      editSongPositionRequested.postMessage({
        targetFileName: selectedFile.name,
        oldPosition: song.currentPosition,
        newPosition: song.currentPosition - 1
      });
    }
  };

  // Obsługa przycisku "w dół"
  const moveSongDown = (e) => {
    e.stopPropagation();
    editSongPositionRequested.postMessage({
      targetFileName: selectedFile.name,
      oldPosition: song.currentPosition,
      newPosition: song.currentPosition + 1
    });
  };

  // Handler for section clicks in remote control mode
  const handleSectionClick = (sectionIndex) => {
    if (!isPresentationControlPanelVisible || !publishPresentationState) return;
    const line = song.sections[sectionIndex]?.content?.[0]?.[0]?.lineNumber;
    revealSongSection(document.getElementById(`section-s${song.currentPosition}-l${line}`));
    
    publishPresentationState({
      fileName: selectedFile.name,
      songNumber: song.currentPosition,
      sectionIndex: sectionIndex,
      sectionNumber: sectionIndex + 1,
      chordsVisible: sharedPresentationState?.chordsVisible,
      blankScreen: false
    });
  };

  // Per-section screen and group icons, as in Hosanna; they exist only in group mode.
  const sectionActions = direct && isCommonSessionEnabled && !isReadOnly ? {
    sectionPresented: sharedPresentationState?.kind === 'section',
    present: sectionIndex => {
      const line = song.sections[sectionIndex]?.content?.[0]?.[0]?.lineNumber;
      revealSongSection(document.getElementById(`section-s${song.currentPosition}-l${line}`));
      publishPresentationState({fileName: selectedFile.name, songNumber: song.currentPosition,
        kind: 'section', sectionIndex, blankScreen: false});
    },
    group: (sectionIndex, line, elementId) => {
      const songStart = song.lineStart ?? song.lines?.[0]?.lineNumber ?? 1;
      if (line === songStart) {
        line = song.sections[sectionIndex].content.flat().find(item => item.lineNumber > songStart)?.lineNumber ?? line;
      }
      publishGroupSelection(selectedFile.name, song.currentPosition, line);
      const element = document.getElementById(elementId);
      element?.focus?.({preventScroll: true});
      revealSongSection(element);
    }
  } : null;

  return (
    <div id={`song-${song.currentPosition}`} className={`song ${isCurrentPresentationSong ? 'current-presentation-song' : ''}${isGroupTarget ? ' song-group-target' : ''}${isWholeGroupSong ? ' song-group-target--whole' : ''}${isPresentationTarget && sharedPresentationState?.kind !== 'section' ? ' song-presentation-target song-presentation-target--whole' : ''}${dragOver ? ' song-drag-over' : ''}`}
      draggable={dragEnabled || undefined}
      onDragStart={dragEnabled ? event => {event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', String(song.currentPosition));} : undefined}
      onDragOver={dragEnabled ? event => {event.preventDefault(); event.dataTransfer.dropEffect = 'move'; setDragOver(true);} : undefined}
      onDragLeave={dragEnabled ? () => setDragOver(false) : undefined}
      onDrop={dragEnabled ? event => {
        event.preventDefault(); setDragOver(false);
        const oldPosition = parseInt(event.dataTransfer.getData('text/plain'), 10);
        if (oldPosition > 0 && oldPosition !== song.currentPosition) editSongPositionRequested.postMessage({
          targetFileName: selectedFile.name, oldPosition, newPosition: song.currentPosition});
      } : undefined}>
      <h3 ref={headerRef} className="song-header">
        <span className="song-header-fixed song-position">
        {isReadOnly ? (
          <span>{song.currentPosition}</span>
        ) : !editPosition ? (
          <span
            className="song-number"
            id={`song-number-${index}`}
            onClick={(e) => {
              // Edycja pozycji tylko gdy wszystkie piosenki są zwinięte i nie ma aktywnego panelu zdalnego sterowania
              if (areAllSongsCollapsed && !isPresentationControlPanelVisible) {
                e.stopPropagation();
                setEditPosition(true);
                setNewPosition(song.currentPosition); // Reset przy każdym otwarciu
              }
            }}
            title={areAllSongsCollapsed && !isPresentationControlPanelVisible ? "Kliknij, aby zmienić pozycję piosenki" : (isPresentationControlPanelVisible ? "Edycja pozycji niedostępna w trybie zdalnego sterowania" : "Zwiń wszystkie piosenki, aby edytować pozycję")}
            style={{ cursor: (areAllSongsCollapsed && !isPresentationControlPanelVisible) ? 'pointer' : 'default' }}
          >{song.currentPosition}</span>
        ) : (
          <span className="position-editor" onClick={(e) => e.stopPropagation()}>
            <input
              // type="number"
              value={newPosition}
              onChange={(e) => {
                // Pozwól na puste pole - konieczne do usuwania cyfr
                const value = e.target.value;
                if (value === '') {
                  // Ustaw pustą wartość, żeby można było skasować wszystkie cyfry
                  setNewPosition('');
                } else {
                  const numValue = parseInt(value, 10);
                  if (!isNaN(numValue) && numValue > 0) {
                    setNewPosition(numValue);
                  }
                }
              }}
              min="1"
              style={{ width: '40px', textAlign: 'center' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveNewPosition();
                if (e.key === 'Escape') {
                  setEditPosition(false);
                  setNewPosition(song.currentPosition); // Reset przy anulowaniu
                }
              }}
              onBlur={() => {
                // Jeśli pole jest puste, użyj oryginalnej wartości
                if (newPosition <= 0) {
                  setNewPosition(song.currentPosition);
                }
              }}
              autoFocus
            />
            <button
              onClick={saveNewPosition}
              title="Zapisz nową pozycję"
              style={{ marginLeft: '2px', padding: '0 5px' }}
            >✓</button>
          </span>
        )}.</span>
        <span className="song-title-group">
          <button onClick={event => {setBodyRequested(true); toggleCollapse(event);}} className="collapse-button song-collapse-chevron"
            title={isCollapsed ? "Rozwiń" : "Zwiń"} aria-label={isCollapsed ? "Rozwiń" : "Zwiń"} aria-expanded={!isCollapsed}>
            {isCollapsed ? <FaChevronRight aria-hidden="true"/> : <FaChevronDown aria-hidden="true"/>}
          </button>
          <button type="button"
            onClick={titleClickHandler}
            className="title" 
            id={`song-title-${song.currentPosition}`}
            aria-expanded={!isCollapsed}
            title={`${isCollapsed ? 'Rozwiń' : 'Zwiń'}: ${song.title}`}
          >{song.title}</button>
          <FavoriteButton entry={libraryEntry}/>
        </span>
        {areChordsVisible && <span className={'song-header-metadata' + (hideMetadata ? ' song-metadata-hidden' : '')} aria-hidden={hideMetadata}>
          {headerMetadata.filter(entry => !entry.youtube).map((entry, i) => {
            if (entry.url) {
              return <a key={'meta-' + i} href={entry.url} target="_blank" rel="noopener noreferrer" title={entry.url} aria-label={entry.youtube ? 'Otwórz w YouTube' : 'Otwórz link'} tabIndex={hideMetadata ? -1 : undefined}>
                {entry.youtube ? <FaYoutube/> : <FaExternalLinkAlt/>}
              </a>;
            }
            return <span className={entry.key === 'artist' ? 'artist' : 'Tag'} title={entry.value} key={'meta-' + i}>{entry.text}</span>;
          })}
        </span>}

        {areChordsVisible && !isPresentationControlPanelVisible && !isCollapsed && (
          <span className="song-header-fixed song-key-controls" aria-busy={isTransposing}>
            <span className="song-transpose-control">
            <button className="transpose-button" disabled={isTransposing} onClick={(e) => handleWholeStep(-2, e)} title="Obniż o cały ton">
              <BsChevronDown />
            </button>
            <div className="song-key-select"
                 onClick={(e) => e.stopPropagation()} // Stop propagation for clicks within the Select container
            >
              {choosingKey ? <Select
                classNamePrefix="song-key"
                value={selectedOption}
                onChange={handleSelectChange} // Use new handler
                options={keyOptions}
                isSearchable={false} // diable for mobile - no keyboard
                isDisabled={isTransposing}
                components={{DropdownIndicator: null, IndicatorSeparator: null}}
                styles={selectStyles}
                getOptionLabel={getOptionLabel}
                formatOptionLabel={formatOptionLabel}
                inputId={`key-select-${index}`} // Add unique ID for accessibility/focus
                menuPlacement="auto" // Better positioning
                maxMenuHeight={480}
                aria-label="Tonacja"
                autoFocus
                defaultMenuIsOpen
                onMenuClose={() => {setChoosingKey(false); requestAnimationFrame(() => keyTriggerRef.current?.focus());}}
              /> : <button ref={keyTriggerRef} type="button" role="combobox" aria-expanded={false} aria-haspopup="listbox" aria-label="Tonacja" disabled={isTransposing}
                className="song-key-trigger" onClick={() => setChoosingKey(true)}
                onKeyDown={event => {if (['ArrowDown', 'ArrowUp'].includes(event.key)) {event.preventDefault(); setChoosingKey(true);}}}>
                {selectedOption ? formatOptionLabel(selectedOption, {context: 'value'}) : (displayKey || '—')}
              </button>}
            </div>
            <button className="transpose-button" disabled={isTransposing} onClick={(e) => handleWholeStep(2, e)} title="Podnieś o cały ton">
              <BsChevronUp />
            </button>
            </span>
            <span className={'notation' + (hideNotation ? ' song-metadata-hidden' : '')} aria-hidden={hideNotation} onClick={isReadOnly ? undefined : toggleNotation} style={{cursor:isReadOnly ? 'default' : 'pointer', marginLeft:'5px', verticalAlign: 'middle'}}
                  title={isReadOnly ? (song.notation === 'polish' ? 'Notacja polska' : 'Notacja międzynarodowa') : (song.notation === 'polish' ? "Przełącz na notację międzynarodową" : "Przełącz na notację polską")}>
              {song.notation === 'polish' ? <span className="fi fi-pl"></span> : '🌎'}
            </span>
          </span>
        )}
        {/* Everything on the right edge: YouTube, then group / presentation / overflow, as in Hosanna. */}
        <span className="song-header-fixed song-header-actions">
          {!isPresentationControlPanelVisible && headerMetadata.filter(entry => entry.youtube).map((entry, i) => {
            const embed = youtubeEmbedUrl(entry.url);
            return embed ? <button type="button" className="song-youtube-button" key={'youtube-' + i}
              title={entry.url} aria-label="Odtwórz w YouTube"
              onClick={() => onYoutubeOpen({src: embed, title: song.title})}><FaYoutube/></button> :
              <a className="song-youtube-button" key={'youtube-' + i} href={entry.url} target="_blank"
                rel="noopener noreferrer" title={entry.url} aria-label="Otwórz w YouTube"><FaYoutube/></a>;
          })}
          {direct && isCommonSessionEnabled && (!isReadOnly
            ? <button type="button" className={`song-header-action song-header-action--group${isGroupTarget ? ' is-active' : ''}`}
              aria-label={`Wybierz dla grupy: ${song.title}`} aria-pressed={isGroupTarget}
              title={isGroupTarget ? 'Ta pieśń jest wybrana dla grupy' : 'Wybierz pieśń dla grupy'}
              onClick={() => publishGroupSelection(selectedFile.name, song.currentPosition,
                song.lineStart ?? song.lines?.[0]?.lineNumber ?? 1)}><IconUsers/></button>
            : isGroupTarget && <span className="song-header-action song-header-action--group is-active song-header-indicator"
              role="img" aria-label={`Aktualnie wspólna pieśń: ${song.title}`} title="Ta pieśń jest wybrana dla grupy"><IconUsers/></span>)}
          {direct && isCommonSessionEnabled && (!isReadOnly
            ? <button type="button" className={`song-header-action song-header-action--presentation${isPresentationTarget ? ' is-active' : ''}`}
              aria-label={`Prezentuj: ${song.title}`} aria-pressed={isPresentationTarget}
              title={isPresentationTarget ? 'Ta pieśń jest prezentowana' : 'Prezentuj całą pieśń'}
              onClick={() => publishPresentationState({fileName: selectedFile.name, songNumber: song.currentPosition, kind: 'song', blankScreen: false})}><IconPresentation/></button>
            : isPresentationTarget && <span className="song-header-action song-header-action--presentation is-active song-header-indicator"
              role="img" aria-label={`Aktualnie prezentowana pieśń: ${song.title}`} title="Ta pieśń jest prezentowana"><IconPresentation/></span>)}
          {!isReadOnly && <SongActionsMenu title={song.title}
            onEdit={() => {selectSong(selectedFile.name, song.currentPosition, undefined, undefined, {produceEvent: false}); setViewType(ViewTypes.MONACO);}}
            onDelete={areAllSongsCollapsed && !isPresentationControlPanelVisible ? handleDeleteSong : undefined}/>}
        </span>
      </h3>
      {transposeError && <p role="alert" className="song-transpose-error">{transposeError}</p>}

      {!isCollapsed && (
        <DeferredSongBody key={selectedFile?.name} defer={deferBody && !bodyRequested}
          estimatedHeight={Math.max(4, song.lines.length * 1.8)}>
        <div className={`lines columns-1${direct ? ' lines--marked' : ''}`}>
          {song.sections.map((section, i) => (
            <SectionElements 
              key={i} 
              section={section} 
              songCurrentPosition={song.currentPosition}
              areChordsVisible={areChordsVisible}
              isLyricSeparatorEnabled={isLyricSeparatorEnabled}
              isPresentationControlPanelVisible={isPresentationControlPanelVisible}
              isCurrentSection={isCurrentPresentationSong && i === currentSectionIndex}
              isPresentedSection={isPresentationTarget && (presentedSectionIndex < 0 ? sharedPresentationState?.kind !== 'section' : i === presentedSectionIndex)}
              isGroupSection={isGroupTarget && (isWholeGroupSong || i === groupedSectionIndex)}
              isActiveGroupSection={isGroupTarget && section?.content?.flat().some(line => line.lineNumber === sharedGroupSelection?.line)}
              songTitle={song.title}
              sectionActions={sectionActions}
              onSectionClick={handleSectionClick}
              sectionIndex={i}
            />
          ))}
        </div>
        </DeferredSongBody>
      )}
    </div>
  );
};

export default SongComponent;
